import React, { useState, useEffect } from 'react';
import Header from '../components/landing/Header';
import TopBar from '../components/landing/TopBar';
import Footer from '../components/landing/Footer';
import EnquiryModal from '../components/landing/EnquiryModal';
import { Briefcase, TrendingUp, Users, Award, CheckCircle } from 'lucide-react';
import api from '../utils/axios';

const Management = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isInquiryOpen, setIsInquiryOpen] = useState(false);
    const [content, setContent] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        fetchContent();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchContent = async () => {
        try {
            const res = await api.get('/content');
            const contentMap = {};
            res.data.data.forEach(item => {
                contentMap[item.key] = item.value;
            });
            setContent(contentMap);
        } catch (err) {
            console.error("Failed to load web content", err);
        }
    };

    const specializations = [
        { name: 'Marketing Management', icon: TrendingUp, desc: 'Digital marketing and global brand strategies.' },
        { name: 'Financial Management', icon: Briefcase, desc: 'Corporate finance and investment banking.' },
        { name: 'Human Resource Management', icon: Users, desc: 'Organizational behavior and talent acquisition.' },
        { name: 'Operations Management', icon: Award, desc: 'Supply chain and logistics optimization.' }
    ];

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            {!isScrolled && <TopBar data={content} />}
            <Header 
                isScrolled={isScrolled} 
                onApply={() => setIsInquiryOpen(true)}
                logo={content.site_logo}
                email={content.contact_email}
                phone={content.contact_phone}
                applyUrl={content.apply_url}
                colleges={content.colleges_list}
                placements={content.placements_list}
                research={content.research_list}
                journals={content.journals_list}
                innovations={content.innovations_list}
            />

            {/* Internal Hero */}
            <header className="relative h-[400px] bg-slate-900 flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-[#003366]/80 z-10"></div>
                <img 
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2000&auto=format&fit=crop" 
                    className="absolute inset-0 w-full h-full object-cover"
                    alt="Management"
                />
                <div className="max-w-7xl mx-auto px-4 relative z-20 w-full">
                    <h1 className="text-5xl md:text-6xl font-black text-white uppercase tracking-tighter">
                        Faculty of <br /><span className="text-amber-400">Management</span>
                    </h1>
                    <div className="h-1.5 w-24 bg-[#cc0000] mt-6"></div>
                </div>
            </header>

            {/* Content Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                        <div className="lg:col-span-2 space-y-8">
                            <h2 className="text-3xl font-black uppercase text-slate-900 tracking-tight">Leaders for a Global Economy</h2>
                            <p className="text-slate-600 leading-relaxed text-lg font-medium">
                                Our Management programs focus on developing ethical, skilled leaders capable of navigating the complexities of the modern business world. Through case studies and industry interaction, we prepare our students to drive growth and innovation.
                            </p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8">
                                {specializations.map((spec, i) => (
                                    <div key={i} className="p-8 border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-2xl hover:-translate-y-1 transition-all group">
                                        <spec.icon size={40} className="text-[#cc0000] mb-6 group-hover:scale-110 transition-transform" />
                                        <h3 className="text-xl font-black uppercase mb-4">{spec.name}</h3>
                                        <p className="text-sm text-slate-500 font-medium leading-relaxed">{spec.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sidebar Info */}
                        <div className="space-y-8">
                            <div className="bg-[#cc0000] p-10 text-white rounded-sm shadow-xl">
                                <h3 className="text-xl font-black uppercase mb-8 border-b border-white/10 pb-4">Key Highlights</h3>
                                <ul className="space-y-4">
                                    {['Global Case Studies', 'Corporate Guest Lectures', 'Summer Internships', '95%+ Placement Success'].map((item, i) => (
                                        <li key={i} className="flex items-center space-x-3 text-sm font-bold uppercase tracking-wider">
                                            <CheckCircle className="text-amber-400" size={18} />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button 
                                    onClick={() => setIsInquiryOpen(true)}
                                    className="w-full mt-10 bg-[#003366] hover:bg-white hover:text-[#003366] py-4 font-black uppercase tracking-widest transition-all"
                                >
                                    Download Brochure
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer data={content} />

            <EnquiryModal isOpen={isInquiryOpen} onClose={() => setIsInquiryOpen(false)} />
        </div>
    );
};

export default Management;
